import { useEffect, useState, type FormEvent } from 'react'

const games = {
  tucked: { name: 'Tucked', itch: 'tucked', price: '$2.99', description: 'A small suitcase. A satisfying fit.' },
  isleink: { name: 'Isle Ink', itch: 'isle-ink', price: '$2.99', description: 'Draw the islands. Shape your score.' },
  tidecipher: { name: 'Tidecipher', itch: 'tidecipher', price: '$2.99', description: 'Read the numbers. Explore the depths.' },
  soulweld: { name: 'Soulweld', itch: 'soulweld', price: '$4.99', description: 'One more run. One stronger build.' },
} as const

export function isPlayableSlug(slug: string): slug is keyof typeof games { return Object.hasOwn(games, slug) }

export function PlayPage({ slug }: { slug: keyof typeof games }) {
  const game = games[slug]
  const checkoutParams = new URLSearchParams({ utm_source: 'cottonray', utm_medium: 'website', utm_campaign: 'paid_web' })
  const incomingParams = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
  for (const name of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
    const value = incomingParams.get(name)
    if (value && /^[a-zA-Z0-9_-]{1,80}$/.test(value)) checkoutParams.set(name, value)
  }
  const [available, setAvailable] = useState<boolean | null>(null)
  const [purchaseLink, setPurchaseLink] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [player, setPlayer] = useState('')
  useEffect(() => {
    const controller = new AbortController()
    void fetch('/api/play/catalog', { signal: controller.signal, cache: 'no-store' })
      .then(async response => {
        if (!response.ok) throw new Error('Unavailable')
        const data = await response.json() as { games?: { slug: string; available: boolean }[] }
        setAvailable(data.games?.find(item => item.slug === slug)?.available === true)
      }).catch(() => { if (!controller.signal.aborted) setAvailable(false) })
    return () => controller.abort()
  }, [slug])

  async function unlock(event: FormEvent) {
    event.preventDefault()
    if (busy || !available) return
    setBusy(true); setError('')
    try {
      const response = await fetch(`/api/play/${slug}/unlock`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purchaseLink }), credentials: 'same-origin',
      })
      if (!response.ok) {
        setError(response.status === 429 ? 'Please wait a minute before trying again.'
          : response.status === 400 || response.status === 403 ? 'Use the download link for this game from your itch.io receipt.'
          : 'We could not verify your purchase right now. Please try again.')
        return
      }
      const result = await response.json() as { playerUrl?: string }
      const expected = `/api/play/${slug}/files/index.html`
      if (result.playerUrl !== expected) throw new Error('Unexpected player address')
      setPurchaseLink(''); setPlayer(expected)
    } catch { setError('Could not connect. Please check your connection and try again.') }
    finally { setBusy(false) }
  }

  return <section className="paid-play studio-wrap">
    <style>{`
      .paid-play { padding-block: 56px 80px; max-width: 1000px; }
      .paid-play h1 { font-size: clamp(36px,7vw,64px); margin: 20px 0 12px; letter-spacing: -.04em; }
      .paid-play p { color: var(--studio-muted); line-height: 1.7; }
      .paid-play__panel { margin-top: 32px; padding: 28px; border: 1px solid #45454f; border-radius: 16px; max-width: 620px; }
      .paid-play__panel h2 { margin-top: 0; font-size: 22px; }
      .paid-play input { display: block; width: 100%; margin: 12px 0; padding: 14px; font: inherit; color: #fff; background: #19191e; border: 1px solid #7c7c89; border-radius: 8px; }
      .paid-play input:focus-visible, .paid-play button:focus-visible { outline: 3px solid var(--studio-accent); outline-offset: 3px; }
      .paid-play button, .paid-play__buy { display: inline-block; padding: 14px 22px; border: 0; border-radius: 999px; background: var(--studio-accent); color: white; font: inherit; font-weight: 700; cursor: pointer; text-decoration: none; }
      .paid-play button:disabled { opacity: .55; cursor: wait; }
      .paid-play__error { color: #ffb4b4 !important; }
      .paid-play iframe { display: block; width: 100%; height: min(80dvh,900px); border: 1px solid #45454f; border-radius: 12px; margin-top: 24px; background: #100d0b; }
      @media(max-width:600px) { .paid-play { padding-inline: 20px; } .paid-play__panel { padding: 20px; } }
    `}</style>
    <a href="/#games">← All games</a>
    <h1>{game.name}</h1><p>{game.description}</p>
    {player ? <><p>Your game is ready. Keep this tab open while you play. Reloading may restart your current puzzle or run.</p>
      <iframe title={`${game.name} game`} src={player} allow="autoplay; fullscreen" allowFullScreen />
      <p>Your receipt link lets you unlock again on your next visit. Browser saves do not transfer between devices.</p></>
      : available ? <>
        {slug === 'tucked' && <div className="paid-play__panel"><h2>Try four puzzles free</h2>
          <p>Pack, rotate, and find room inside a pouch. Try the demo in a desktop browser with a mouse or trackpad. No purchase, sign-up, or installation required.</p>
          <a className="paid-play__buy" href="/api/play/demos/tucked/files/index.html?utm_source=cottonray&utm_medium=play_page&utm_campaign=web_demo">Free demo</a>
        </div>}
        <div className="paid-play__panel"><h2>Play in your browser</h2>
          <p>One purchase. No installation, in-game ads or additional purchases. Checkout is handled by itch.io.</p>
          <p>Keep the game tab open during play: unfinished puzzles and runs may restart when you reload. Saved settings and completed records stay in this browser and do not sync between devices. Clearing browser data removes local saves.</p>
          <a className="paid-play__buy" href={`https://cottonray.itch.io/${game.itch}/purchase?${checkoutParams}`} target="_blank" rel="noopener noreferrer">Buy · {game.price}</a>
        </div>
        <form className="paid-play__panel" onSubmit={event => { void unlock(event) }}>
          <h2>Already purchased?</h2><p>Open your itch.io receipt, copy its download link, and paste it below. Keep this link private.</p>
          <label htmlFor="purchase-link">Your itch.io download link</label>
          <input id="purchase-link" type="password" autoComplete="off" maxLength={512} value={purchaseLink} onChange={event => setPurchaseLink(event.target.value)} required />
          <button type="submit" disabled={busy || !purchaseLink.trim()}>{busy ? 'Verifying…' : 'Unlock'}</button>
          {error && <p role="alert" className="paid-play__error">{error}</p>}
        </form>
      </> : <div className="paid-play__panel" role="status"><h2>{available === null ? 'Checking availability…' : 'Browser edition in preparation'}</h2>
        <p>{available === null ? 'One moment, please.' : 'We are getting this game ready for browser play. Web purchases are not open yet.'}</p></div>}
  </section>
}
